/**
 * KB read-activity API.
 *
 * Separate from kb.ts because these are reporting endpoints over the audit log
 * rather than CRUD over entries, and they answer a different question: how much
 * the knowledge base is being read, not what is in it.
 */

import { apiFetch, apiFetchWithMeta } from './client.js'

/**
 * Total reads on one UTC day.
 *
 * Rows are SPARSE — only days with reads come back. The view builds the full
 * grid and fills the gaps with zeros.
 */
export type KBReadHeatmapRow = {
  /** UTC calendar day, `YYYY-MM-DD`. */
  day: string
  reads: number
}

/** Total KB reads per day for the selected project. */
export const fetchKBReadHeatmap = (params?: {
  days?: number
  type?: string
  /** 'mine' is the viewer's own activity; 'team' is everybody else's. */
  actor?: 'all' | 'mine' | 'team'
}): Promise<KBReadHeatmapRow[]> =>
  // apiFetchWithMeta, not apiFetch: array payloads must go through it
  // (ST-no-apifetch-list). This endpoint returns no `meta` — the result is a
  // sparse grid, not a page — so the envelope's data is unwrapped here.
  apiFetchWithMeta<KBReadHeatmapRow[]>('/api/v2/insights/kb-reads', params).then((r) => r.data)

/** One entry read on a given day. `title` is null if the entry was deleted. */
export type KBReadDetailRow = {
  entryId: string
  title: string | null
  category: string | null
  reads: number
}

/**
 * The entries behind one heatmap cell.
 *
 * `day` must be the exact `YYYY-MM-DD` string the grid returned, so the two
 * queries cannot disagree about which day a read belongs to.
 */
export const fetchKBReadDetail = (params: {
  day: string
  type?: string
  /** Must match the grid the cell was clicked in, or the evidence disagrees. */
  actor?: 'all' | 'mine' | 'team'
}): Promise<KBReadDetailRow[]> =>
  apiFetchWithMeta<KBReadDetailRow[]>('/api/v2/insights/kb-reads/detail', params).then(
    (r) => r.data,
  )

// ─── Graph ──────────────────────────────────────────────────────────

export type KBGraphNode = { id: string; type: string; title: string }

export type KBGraphEdge = {
  from: string
  to: string
  weight: number
  /** 'relation' = a human drew it with `grizzly kb link`; 'tag' = derived. */
  kind: 'relation' | 'tag'
  /** Tags that produced a derived edge, strongest first. Empty for relations. */
  via: string[]
}

export type KBGraphData = { nodes: KBGraphNode[]; edges: KBGraphEdge[] }

/** The KB as a graph for the selected project. Not paginated — see the spec. */
export const fetchKBGraph = (params?: { edges?: number }): Promise<KBGraphData> =>
  apiFetch<KBGraphData>('/api/v2/insights/kb-graph', params)

// ─── Analytics ──────────────────────────────────────────────────────

/** One day of one series. */
export type KBDailyCount = { day: string; count: number }

/**
 * The four series, split relative to WHOEVER IS ASKING.
 *
 * `mine` is the authenticated viewer, `team` is everybody else. The server never
 * sends an identity, because there is nothing to send: the split is implied by
 * the credentials on the request.
 */
export type KBAnalytics = {
  days: number
  reads: { mine: { day: string; reads: number }[]; team: { day: string; reads: number }[] }
  writes: { mine: { day: string; created: number }[]; team: { day: string; created: number }[] }
}

/** All four daily series in one call, so they cannot disagree about the window. */
export const fetchKBAnalytics = (params?: { days?: number }): Promise<KBAnalytics> =>
  apiFetch<KBAnalytics>('/api/v2/insights/kb-analytics', params)
